import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CONTENT_TYPES,
  IMAGE_MODE,
  TRUCATE_STRING,
} from '../../../constants/contentTypes';
import { NO_IMAGE_DEFAULT } from '../../../constants/noImage';
import useContentNavigation from '../../../customHooks/useContentNavigation';
import useRouter from '../../../customHooks/useRouter';
import useWidgetTranslation from '../../../customHooks/useWidgetTranslation';
import { findImageSettingByType } from '../../../utils/appSettingUtils';
import * as actions from './actions';
import { SHOW_RATING_IMAGE, TITLES, types } from './constants';
import * as hooks from './hooks';
import * as selectors from './selectors';

// #region /////// COMPONENTS ///////
import {
  ContentEpisodes,
  ContentOverview,
  ContentRelated,
  GenericModal,
  isTokenFromBR,
} from '@tbx/experience-widgets-lib';
import { isDesktop } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { SectionErrorBoundary } from '../../../HOCs/WithErrorBoundary';
import Footer from '../../../components/Footer';
import IFrameComponent from '../../../components/IFrameComponent';
import Seo from '../../../components/Seo';
import SocialSharingList from '../../../components/SocialSharingList';
import { ERROR_CODE } from '../../../constants/errorCode';
import { RAITINGS_DEFAULT } from '../../../constants/ratings';
import {
  getSeoTitleInSection,
  validateAccessToContentByUrns,
  validateUrlRedirect,
} from '../../../utils/ContentUtils';
import { validateFavoriteContent } from '../../../utils/ProfileUtils';
import { getContentImageByMode } from '../../../utils/imageUtils';
import { truncateString } from '../../../utils/objectUtils';
import { convertPath } from '../../../utils/pathRoutesUtils';
import { PATH_ERROR } from '../../AppSectionRouter/constants';
import {
  addFavoriteContentByProfile,
  deleteFavoriteContentByProfile,
} from '../../ProfileManager/actions';
import { selectCurrentContentPosition } from '../../ProfileManager/selectors';
import useBrandInfo from '../../../customHooks/useBrandInfo';
import { getCurrentEpisodeBySerie } from '../HomeSection/actions';
import useClientConfigs from '../../../customHooks/useClientConfigs';
import { useScript } from '../../../customHooks/useScript';
import ExtendedFooter from '../../../components/ExtendedFooter';
// #endregion

// #region /////// VARIABLES goBackFunction ///////
const redirect = {
  url: '/',
  countGoBack: 0,
  boolGoBack: false,
};
// #endregion

function ContentOverViewSection({ accessToken, storeTime }) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const contentID = router.query.contentID;
  const mainDiv = React.useRef();
  const translationTexts = useWidgetTranslation(TITLES);
  const [showFooter, setShowFooter] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedSeason, setSelectedSeason] = React.useState(1);
  const dispatch = useDispatch();
  const BRAND_INFO = useBrandInfo();
  const {
    showContentDescription,
    hasPayService,
    hasDirectAccessToAddOns,
    useExtendedFooter,
    CustomFooter,
    showNetworksContent,
    validateHaveAccess,
    enabledResponsiveWeb,
  } = useClientConfigs();

  // #region /////// SELECTORS ///////
  const selectedContent = useSelector((state) =>
    selectors.selectContentWidget(state),
  );
  const selectedContentRelated = useSelector((state) =>
    selectors.selectContentRelatedWidget(state),
  );
  const selectContentEpisodes = useSelector((state) =>
    selectors.selectContentEpisodesWidget(state),
  );
  const authenticated = useSelector((state) =>
    selectors.selectAuthenticated(state),
  );
  const appSettings = useSelector((state) =>
    selectors.selectAppSettings(state),
  );
  const redirectUrl = useSelector((state) =>
    selectors.selectContentsUrlByID(state),
  );
  const currentContentPosition = useSelector((state) =>
    selectCurrentContentPosition(state),
  );
  const selectError = useSelector((state) => selectors.selectError(state));
  const favorites = useSelector((state) =>
    selectors.selectFavoritesContents(state),
  );
  const favoritesIsFetching = useSelector((state) =>
    selectors.selectFavoritesIsFetching(state),
  );
  const PATHS = useSelector((state) => selectors.selectPathList(state));
  const selectRatingImages = useSelector((state) =>
    selectors.selectRatingImages(state),
  );
  const selectUserUrns = useSelector((state) =>
    selectors.selectUserUrns(state),
  );
  const selectTotalEpisodes = useSelector((state) =>
    selectors.selectTotalEpisodes(state),
  );
  const contentAccess = useSelector((state) =>
    selectors.selectContentAccess(state),
  );
  const { mobileAppUrls } = appSettings;
  const showCustomMetaTags = useSelector((state) =>
    selectors.selectShowCustomMetaTags(state),
  );
  // #endregion

  // #region ///// HOOK EFFECTS /////
  useScript(true);
  hooks.useDidMount(
    accessToken,
    contentID,
    mainDiv,
    authenticated,
    PATHS,
    selectRatingImages,
  );
  const toastAdd = hooks.useToastAdd();
  hooks.useStoreTimeOnClickBack(storeTime, redirect, router, PATHS);
  const { contentEpisodesState, currentSeason } = hooks.useBookmarkEpisodes(
    selectContentEpisodes,
    currentContentPosition,
  );
  const contentSeasonsData = selectedContent?.episodesBySeason?.map(
    ({ episodes, ...rest }) => rest,
  );
  // #endregion

  const { packageUpgradeMessages, enableContentPage } = appSettings;

  const { handlePlayContent, handleSelectContent } = useContentNavigation({
    authenticated,
    accessToken,
    PATHS,
    mobileAppUrls,
    hasPayService,
    hasDirectAccessToAddOns,
    enableContentPage,
    packageUpgradeMessages,
    redirectUrl,
    enabledResponsiveWeb,
    onNavigate: () => setShowFooter(false),
    onGetContentsUrlById: (id) =>
      dispatch(actions.getContentsUrlById(accessToken, id)),
    onGetEpisodeBySerie: (id, ...args) => {
      // Usar la acción antigua que el saga usa para Play Series
      dispatch(getCurrentEpisodeBySerie(id, router, PATHS.player));
    },
    currentContentPosition,
    selectUserUrns,
  });

  const onClickPlayerButton = () => {
    redirect.countGoBack = 0;
    handlePlayContent(selectedContent);
  };

  const onClickEpisode = (contentId) => {
    if (!contentId || contentId === 'undefined' || contentId === 'null')
      return false;

    redirect.countGoBack = 0;
    const episodesBase = authenticated
      ? contentEpisodesState
      : selectContentEpisodes;
    const episodeContent = episodesBase?.find((c) => c.id === contentId) || {
      id: contentId,
      contentType: CONTENT_TYPES.EPISODE,
      entitlementTypes:
        currentContentPosition?.currentContent?.entitlementTypes,
    };
    handlePlayContent(episodeContent, true);
  };

  const onClickContentSelected = (ev, contentid) => {
    ev.preventDefault();
    if (!contentid || contentid === 'undefined' || contentid === 'null')
      return false;

    redirect.countGoBack = 0;
    // setShowFooter is now handled by onNavigate hook inside handleSelectContent

    const relatedContent = selectedContentRelated?.find(
      (c) => c.id === contentid,
    ) || {
      id: contentid,
      contentType:
        selectedContentRelated?.[0]?.contentType || CONTENT_TYPES.MOVIE,
    };
    handleSelectContent(relatedContent);
  };

  const handleSeasonChange = async (season) => {
    setSelectedSeason(season);
    setCurrentPage(1); // Resetear la página al cambiar de temporada

    // Disparar la acción para cargar los episodios de la nueva temporada
    handleLoadMoreEpisodes(season, true);
  };

  const handleLoadMoreEpisodes = (
    season = selectedSeason,
    isSeasonChange = false,
  ) => {
    const nextPage = isSeasonChange ? 1 : currentPage + 1;

    dispatch({
      type: types.WIDGET_FETCH_SUCCESS,
      content: selectedContent,
      page: nextPage,
      season,
      isSeasonChange,
    });

    setCurrentPage(nextPage);
  };

  const goBackFunction = React.useCallback(async () => {
    if (
      (redirect.countGoBack >= 2 && redirect.boolGoBack) ||
      router.location.state?.oneBack.path === PATHS.player ||
      !router.location.state?.oneBack.path
    ) {
      redirect.countGoBack = 0;
      return router.push(redirect.url);
    }
    redirect.countGoBack = redirect.countGoBack + 1;
    return router.goBack();
  }, [router]);

  const catchError = (error) => {
    return router.push(PATH_ERROR, { error });
  };

  React.useEffect(() => {
    // REDIRECT TO FICHA FUTURA
    if (
      selectedContent?.live &&
      selectedContent?.contentType === CONTENT_TYPES.SPORT
    ) {
      router.push(convertPath(PATHS.live, selectedContent?.id));
    }

    if (
      ERROR_CODE[selectError.code] ||
      (!!selectError.code && !!selectError.details && !!selectError.message)
    ) {
      toastAdd(selectError);
      goBackFunction();
    }
  }, [selectError]);

  React.useEffect(() => {
    if (
      (currentContentPosition?.currentContent?.serie?.id === contentID ||
        currentContentPosition?.currentContent?.id === contentID) &&
      !currentContentPosition?.currentContent?.entitlementTypes[0]
    ) {
      toastAdd('NOT_ENTITLEMENTS');
    }
  }, [currentContentPosition?.currentContent]);

  React.useEffect(() => {
    if (validateHaveAccess) {
      dispatch(actions.getContentsAccess(accessToken, contentID));
    }
  }, [validateHaveAccess, accessToken, contentID, dispatch]);

  const onClickMyListButton = (contentId, favoritesAdded) => {
    favoritesAdded
      ? dispatch(deleteFavoriteContentByProfile(contentId))
      : dispatch(addFavoriteContentByProfile(contentId));
  };

  const contentNoImageProps = (imageMode) => {
    const { w, h } = findImageSettingByType(appSettings.imageSizes, imageMode);
    return {
      width: w || NO_IMAGE_DEFAULT(imageMode).width,
      height: h || NO_IMAGE_DEFAULT(imageMode).height,
      backgroundColorOne: NO_IMAGE_DEFAULT().backgroundColorOne,
      backgroundColorTwo: NO_IMAGE_DEFAULT().backgroundColorTwo,
    };
  };

  const handleSearchContentByFilter = React.useCallback(
    async (searchText, filterName) => {
      router.push(`${PATHS.search}?${filterName}=${searchText}`);
    },
    [router],
  );

  const inFavoritesAdded = validateFavoriteContent(favorites, contentID);
  const truncatedDescription = truncateString(
    selectedContent?.description,
    TRUCATE_STRING.METATAGS_DESCRIPTION,
  );
  const imageUrl = getContentImageByMode(selectedContent?.images, 'THUMB');

  const ratingsList = {};
  Object.entries(RAITINGS_DEFAULT).forEach(([key, value]) => {
    ratingsList[key] = t(value);
  });

  const mustShowRatingImage = () => {
    return SHOW_RATING_IMAGE || isTokenFromBR(accessToken?.access_token);
  };

  const shouldShowBuySubscription = useMemo(() => {
    if (!authenticated || selectedContent?.free) {
      return true;
    }

    if (validateHaveAccess) {
      return contentAccess.access;
    } else {
      return validateAccessToContentByUrns(
        selectedContent?.urn,
        selectUserUrns,
      );
    }
  }, [
    authenticated,
    selectedContent,
    selectUserUrns,
    contentAccess,
    validateHaveAccess,
  ]);

  const shouldDisableButton =
    authenticated &&
    !currentContentPosition?.currentContent?.entitlementTypes[0];

  const renderFooter = () => {
    if (!showFooter) {
      return <></>;
    }
    if (CustomFooter) {
      return <CustomFooter mobileAppUrls={mobileAppUrls} />;
    }
    if (useExtendedFooter) {
      return <ExtendedFooter />;
    }
    return <Footer />;
  };

  return (
    <div ref={mainDiv} className="main-content">
      {!showCustomMetaTags && selectedContent && (
        <Seo
          title={getSeoTitleInSection(
            selectedContent,
            t,
            i18n.language,
            'Repetición',
            BRAND_INFO,
          )}
          contentTitle={getSeoTitleInSection(
            selectedContent,
            t,
            i18n.language,
            'Repetición',
            BRAND_INFO,
          )}
          description={truncatedDescription}
          image={imageUrl?.url}
        />
      )}
      <div
        // eslint-disable-next-line react/no-unknown-property
        onLoad={() => setShowFooter(true)}
        className="ContentOverviewSection"
      >
        {
          <ContentOverview
            translationTexts={translationTexts}
            content={selectedContent}
            onClickPlayerButton={onClickPlayerButton}
            onClickBackButton={goBackFunction}
            catchError={catchError}
            isLoading={!selectedContent}
            contentNoImageProps={contentNoImageProps(IMAGE_MODE.POSTER)}
            currentContentPosition={currentContentPosition}
            disabledPlayButton={shouldDisableButton}
            inFavoritesAdded={inFavoritesAdded}
            favoritesIsFetching={favoritesIsFetching}
            handleSearchContentByFilter={handleSearchContentByFilter}
            onClickMyListButton={authenticated && onClickMyListButton}
            ratingImages={selectRatingImages}
            mustShowRatingImage={mustShowRatingImage}
            redirectContent={validateUrlRedirect(currentContentPosition)}
            showPlayIcon={false}
            imageMode={IMAGE_MODE.POSTER}
            showRating={showContentDescription}
            showChannel={showNetworksContent}
            showYear={showContentDescription}
            showDuration={showContentDescription}
            hasAccessTo={shouldShowBuySubscription}
          >
            <GenericModal
              title={translationTexts.share_on}
              // eslint-disable-next-line react/no-children-prop
              children={<SocialSharingList />}
              isModal={false}
              placement="right"
            />
          </ContentOverview>
        }
      </div>
      {selectedContent && selectedContent.statsUrl && (
        <IFrameComponent source={`${selectedContent.statsUrl}`} />
      )}
      <div>
        {selectedContent?.contentType === CONTENT_TYPES.MOVIE &&
          selectedContentRelated &&
          Array.isArray(selectedContentRelated) &&
          selectedContentRelated.length > 0 && (
            <ContentRelated
              content={selectedContentRelated}
              translationTexts={translationTexts}
              contentNoImageProps={contentNoImageProps(IMAGE_MODE.POSTER)}
              onClickContentSelected={onClickContentSelected}
              isLoading={!selectedContentRelated}
              imageMode={IMAGE_MODE.POSTER}
              catchError={() => {}}
            />
          )}
        {selectedContent?.contentType === CONTENT_TYPES.SERIE && (
          <ContentEpisodes
            translationTexts={translationTexts}
            content={
              authenticated ? contentEpisodesState : selectContentEpisodes
            }
            contentNoImageProps={contentNoImageProps(IMAGE_MODE.THUMB)}
            onClickContentSelected={onClickEpisode}
            isLoading={
              authenticated ? !contentEpisodesState : !selectContentEpisodes
            }
            catchError={catchError}
            countItem={5}
            currentSeason={currentSeason}
            disabledEpisodes={
              authenticated &&
              !currentContentPosition?.currentContent?.entitlementTypes[0]
            }
            contentSeasonsData={contentSeasonsData}
            totalEpisodes={selectTotalEpisodes}
            onLoadMore={handleLoadMoreEpisodes}
            onSeasonChange={handleSeasonChange}
          />
        )}
      </div>
      {renderFooter()}
    </div>
  );
}

export default SectionErrorBoundary(ContentOverViewSection);
